/**
 * The Open Library adapter.
 *
 * One of `enrichment/providers/`' four adapters. It lived inside
 * `app/api/lookup/openlibrary/route.ts` until #407; the route keeps its `GET`, its `getUser()`
 * guard, its 401 and a delegation (ADR 0010), and `enrich.ts` calls `lookupBook` in process
 * instead of opening a cookie-less HTTP call to the app's own origin (#78).
 *
 * **This module must not import `@/account/server`.** The session check belongs to the route.
 */

import { trackError } from '@/lib/errors'

const OPENLIBRARY_API_BASE = 'https://openlibrary.org'

interface OpenLibrarySearchDoc {
  key: string // e.g., "/works/OL123W"
  title: string
  author_name?: string[]
  author_key?: string[]
  first_publish_year?: number
  subject?: string[]
  cover_i?: number // Cover ID for building image URL
  edition_count?: number
}

interface OpenLibrarySearchResult {
  numFound: number
  docs: OpenLibrarySearchDoc[]
}

interface OpenLibraryWork {
  key: string
  title: string
  description?: string | { value: string }
  subjects?: string[]
  covers?: number[]
}

export interface BookResult {
  externalId: string
  title: string
  author?: string
  year?: number
  topics?: string[]
  coverUrl?: string
  description?: string
}

function getCoverUrl(
  coverId: number | undefined,
  size: 'S' | 'M' | 'L' = 'M',
): string | undefined {
  if (!coverId) return undefined
  return `https://covers.openlibrary.org/b/id/${coverId}-${size}.jpg`
}

async function searchBooks(
  query: string,
  author?: string,
): Promise<OpenLibrarySearchDoc[]> {
  let url = `${OPENLIBRARY_API_BASE}/search.json?q=${encodeURIComponent(query)}&limit=5`

  if (author) {
    url += `&author=${encodeURIComponent(author)}`
  }

  const response = await fetch(url)

  if (!response.ok) {
    trackError(new Error(`OpenLibrary search failed: ${response.status}`), {
      provider: 'openlibrary',
      query,
      status: response.status,
    })
    return []
  }

  const data: OpenLibrarySearchResult = await response.json()
  return data.docs || []
}

async function getWorkDetails(
  workKey: string,
): Promise<OpenLibraryWork | null> {
  // workKey is like "/works/OL123W"
  const response = await fetch(`${OPENLIBRARY_API_BASE}${workKey}.json`)

  if (!response.ok) {
    trackError(new Error(`OpenLibrary work fetch failed: ${response.status}`), {
      provider: 'openlibrary',
      workKey,
      status: response.status,
    })
    return null
  }

  return response.json()
}

export async function lookupBook(query: string, author?: string) {
  const searchResults = await searchBooks(query, author || undefined)

  if (searchResults.length === 0) {
    return { bestMatch: null, alternatives: [] }
  }

  // Get details for best match
  const bestDoc = searchResults[0]
  let workDetails: OpenLibraryWork | null = null

  if (bestDoc.key) {
    workDetails = await getWorkDetails(bestDoc.key)
  }

  // Extract description (short summary about the book)
  let description: string | undefined
  if (workDetails?.description) {
    description =
      typeof workDetails.description === 'string'
        ? workDetails.description
        : workDetails.description.value
    // Truncate to a concise summary (around 200 chars)
    if (description && description.length > 200) {
      // Try to break at a sentence boundary
      const truncated = description.slice(0, 200)
      const lastPeriod = truncated.lastIndexOf('.')
      description =
        lastPeriod > 100
          ? truncated.slice(0, lastPeriod + 1)
          : truncated + '...'
    }
  }

  // Combine subjects from search and work details
  // Filter out overly generic topics and limit to 5
  const allTopics = [
    ...(bestDoc.subject || []),
    ...(workDetails?.subjects || []),
  ]

  // Filter out common noise topics and dedupe
  const filteredTopics = allTopics.filter(
    (topic) =>
      // Filter out overly generic/noise topics
      topic.length > 2 &&
      topic.length < 50 &&
      !topic.toLowerCase().includes('accessible book') &&
      !topic.toLowerCase().includes('protected daisy') &&
      !topic.toLowerCase().includes('in library') &&
      !topic.toLowerCase().startsWith('nyt:'),
  )

  // Dedupe and take first 5 (increased from 3)
  const topics = [...new Set(filteredTopics)].slice(0, 5)

  const bestMatch: BookResult = {
    externalId: bestDoc.key.replace('/works/', ''),
    title: bestDoc.title,
    author: bestDoc.author_name?.[0],
    year: bestDoc.first_publish_year,
    topics, // Already limited to 3
    coverUrl: getCoverUrl(bestDoc.cover_i || workDetails?.covers?.[0], 'M'),
    description,
  }

  const alternatives: BookResult[] = searchResults.slice(1).map((doc) => ({
    externalId: doc.key.replace('/works/', ''),
    title: doc.title,
    author: doc.author_name?.[0],
    year: doc.first_publish_year,
    coverUrl: getCoverUrl(doc.cover_i, 'S'),
  }))

  return { bestMatch, alternatives }
}
